/**
 * Status badges.
 *
 * All of them derive their state rather than trusting a stored column, so a screen never shows
 * "open" on an invoice that went overdue at midnight. The derivation is the same `@rentorng/core`
 * function the API and the nightly job use, which is what keeps three surfaces telling one story.
 */

import {
  classifyInvoice,
  daysOverdue,
  deriveLeaseStatus,
  deriveStatus,
  expiryBucket,
  leaseStatusInfo,
  unitStatusInfo,
  type InvoiceStatus,
  type LeaseStatus,
  type PlainDate,
  type UnitStatus,
} from "@rentorng/core";
import { Badge, type Tone } from "./ui";

export interface InvoiceLike {
  dueDate: PlainDate;
  total: number;
  paid: number;
  status: InvoiceStatus;
}

const INVOICE_TONE: Record<InvoiceStatus, Tone> = {
  draft: "muted",
  open: "info",
  part_paid: "warn",
  paid: "success",
  overdue: "danger",
  void: "muted",
  written_off: "muted",
};

const INVOICE_LABEL: Record<InvoiceStatus, string> = {
  draft: "Draft",
  open: "Open",
  part_paid: "Part paid",
  paid: "Paid",
  overdue: "Overdue",
  void: "Void",
  written_off: "Written off",
};

export function InvoiceStatusBadge({
  invoice,
  asOf,
  graceDays = 0,
  showDays = true,
}: {
  invoice: InvoiceLike;
  asOf: PlainDate;
  graceDays?: number;
  showDays?: boolean;
}) {
  const status = deriveStatus(invoice, asOf, graceDays);
  const late = status === "overdue" ? daysOverdue(invoice.dueDate, asOf, graceDays) : 0;
  return (
    <Badge tone={INVOICE_TONE[status]} dot={status === "overdue"}>
      {INVOICE_LABEL[status]}
      {showDays && late > 0 ? ` · ${late}d` : ""}
    </Badge>
  );
}

export function MoneyBucketBadge({
  invoice,
  asOf,
  graceDays = 0,
  dueSoonDays = 30,
}: {
  invoice: InvoiceLike;
  asOf: PlainDate;
  graceDays?: number;
  dueSoonDays?: number;
}) {
  const bucket = classifyInvoice(invoice, asOf, { graceDays, dueSoonDays });
  if (bucket === "settled") return <Badge tone="success">Settled</Badge>;
  if (bucket === "overdue") return <Badge tone="danger" dot>Overdue</Badge>;
  if (bucket === "due_soon") return <Badge tone="warn" dot>Due soon</Badge>;
  return <Badge tone="muted">Upcoming</Badge>;
}

export function LeaseStatusBadge({
  lease,
  asOf,
}: {
  lease: { status: LeaseStatus; startDate: PlainDate; endDate: PlainDate | null };
  asOf: PlainDate;
}) {
  const status = deriveLeaseStatus(lease, asOf);
  const info = leaseStatusInfo(status);
  return (
    <Badge tone={info.tone} dot={status === "expired" || status === "expiring"}>
      {info.label}
    </Badge>
  );
}

export function ExpiryBadge({ endDate, asOf }: { endDate: PlainDate | null; asOf: PlainDate }) {
  if (!endDate) return <Badge tone="muted">Open-ended</Badge>;
  const bucket = expiryBucket(endDate, asOf);
  const tone: Tone = bucket === "expired" || bucket === "d0_30" ? "danger" : bucket === "d31_60" ? "warn" : "muted";
  const label =
    bucket === "expired" ? "Expired" : bucket === "d0_30" ? "≤30 days" : bucket === "d31_60" ? "31–60 days" : bucket === "d61_90" ? "61–90 days" : "Later";
  return <Badge tone={tone}>{label}</Badge>;
}

export function UnitStatusBadge({ status }: { status: UnitStatus }) {
  const info = unitStatusInfo(status);
  return <Badge tone={info.tone === "muted" ? "muted" : info.tone}>{info.label}</Badge>;
}

const MESSAGE_TONE: Record<string, Tone> = {
  sent: "success",
  simulated: "info",
  queued: "muted",
  sending: "muted",
  failed: "danger",
  cancelled: "muted",
};

export function MessageStatusBadge({ status }: { status: string }) {
  return <Badge tone={MESSAGE_TONE[status] ?? "muted"}>{status === "simulated" ? "simulated" : status}</Badge>;
}

const MAINTENANCE_TONE: Record<string, Tone> = {
  open: "danger",
  assigned: "warn",
  in_progress: "warn",
  on_hold: "muted",
  resolved: "success",
  closed: "muted",
  rejected: "muted",
};

export function MaintenanceStatusBadge({ status }: { status: string }) {
  return <Badge tone={MAINTENANCE_TONE[status] ?? "muted"}>{status.replace(/_/g, " ")}</Badge>;
}

export function PriorityBadge({ priority }: { priority: string }) {
  const tone: Tone = priority === "emergency" ? "danger" : priority === "high" ? "warn" : priority === "normal" ? "info" : "muted";
  return <Badge tone={tone}>{priority}</Badge>;
}
